import { defaultGridSettings, SETTINGS_KEY } from '@/app/atoms/gridSettingsAtom';
import { atom } from 'jotai';
import type { Chat } from 'quadratic-shared/typesAndSchemasAI';
import type {
  ContextSegmentSizesState,
  ContextUsageHistoryState,
  ContextUsageState,
  EmptyChatSuggestionsState,
  FailingSqlConnectionsState,
  ImportFilesToGridState,
  PdfImportState,
  PendingUserQuestion,
  PromptSuggestionsState,
  QueuedPrompt,
  UserQuestionAnswers,
  WebSearchState,
} from './types';
import { readCurrentFileAIState } from './sessionState';
import { createDefaultChat } from './types';

// Read the user's "show on startup" preference from localStorage at module
// load so the panel's initial render reflects the saved setting. Without
// this, the atom defaults to false and the panel pops in once
// `initializeAIAnalyst` runs in a post-render effect, causing the grid to
// shift and visibly flicker on every cold file open.
//
// On a refresh in the same tab, the per-tab session record reflects the panel
// state the user last had open for this file, so prefer it to avoid an
// open->closed (or closed->open) flicker before `initializeAIAnalyst` runs.
const getInitialShowAIAnalyst = (): boolean => {
  try {
    if (typeof window === 'undefined') return defaultGridSettings.showAIAnalystOnStartup;

    const sessionState = readCurrentFileAIState();
    if (sessionState) return sessionState.aiAnalystOpen;

    const saved = window.localStorage.getItem(SETTINGS_KEY);
    if (!saved) return defaultGridSettings.showAIAnalystOnStartup;
    const parsed = JSON.parse(saved);
    return typeof parsed?.showAIAnalystOnStartup === 'boolean'
      ? parsed.showAIAnalystOnStartup
      : defaultGridSettings.showAIAnalystOnStartup;
  } catch {
    return defaultGridSettings.showAIAnalystOnStartup;
  }
};

// ============================================================================
// Initialization State
// ============================================================================

/** Whether the AI Analyst has been initialized */
export const aiAnalystInitializedAtom = atom(false);

// ============================================================================
// UI State Atoms
// ============================================================================

/** Whether the AI Analyst panel is visible */
export const showAIAnalystAtom = atom(getInitialShowAIAnalyst());

/** Whether the chat history view is shown */
export const showChatHistoryAtom = atom(false);

/** Active schema connection UUID for SQL context */
export const activeSchemaConnectionUuidAtom = atom<string | undefined>(undefined);

/**
 * Active agent connection UUID whose AI-generated apiSchema browser panel is
 * open. Mirrors `activeSchemaConnectionUuidAtom` for SQL connections; rendered
 * by `AIAnalystAgentConnectionSchema`.
 */
export const activeSchemaAgentConnectionUuidAtom = atom<string | undefined>(undefined);

// ============================================================================
// Loading State Atoms
// ============================================================================

/** Whether an AI request is in progress */
export const loadingAtom = atom(false);

/** Abort controller for the current AI request */
export const abortControllerAtom = atom<AbortController | undefined>(undefined);

/** Message index we're waiting on (for billing limit handling) */
export const waitingOnMessageIndexAtom = atom<number | undefined>(undefined);

/** Queue of prompts waiting to be executed after the current one finishes */
export const promptQueueAtom = atom<QueuedPrompt[]>([]);

/** Whether queue processing is paused by the user */
export const promptQueuePausedAtom = atom(false);

// ============================================================================
// Chat State Atoms
// ============================================================================

/** All saved chats */
export const chatsAtom = atom<Chat[]>([]);

/** Current active chat (internal base atom) */
export const currentChatBaseAtom = atom<Chat>(createDefaultChat());

// ============================================================================
// Feature State Atoms
// ============================================================================

/** Prompt suggestions state */
export const promptSuggestionsAtom = atom<PromptSuggestionsState>({
  abortController: undefined,
  suggestions: [],
});

/** PDF import operation state */
export const pdfImportAtom = atom<PdfImportState>({
  abortController: undefined,
  loading: false,
});

/** Web search operation state */
export const webSearchAtom = atom<WebSearchState>({
  abortController: undefined,
  loading: false,
});

/**
 * In-flight `ask_user_question` tool call. When set, the AI tool loop is
 * paused awaiting the user's answer and the chat renders the interactive
 * question UI. Undefined when no question is awaiting an answer.
 */
export const pendingUserQuestionAtom = atom<PendingUserQuestion | undefined>(undefined);

/**
 * Answers the user has submitted for `ask_user_question` tool calls, keyed by
 * tool call id. Lets the chat render the locked-in selection for a question
 * after it has been answered.
 */
export const userQuestionAnswersAtom = atom<Record<string, UserQuestionAnswers>>({});

/** Import files to grid operation state */
export const importFilesToGridAtom = atom<ImportFilesToGridState>({
  loading: false,
});

/** Failing SQL connections tracking */
export const failingSqlConnectionsAtom = atom<FailingSqlConnectionsState>({
  uuids: [],
  lastResetTimestamp: 0,
});

/** Context usage from AI API responses */
export const contextUsageAtom = atom<ContextUsageState>({
  usage: undefined,
});

/**
 * Bounded history of context usage from AI API responses, oldest first.
 * Capped at CONTEXT_USAGE_HISTORY_LIMIT in actions.ts to keep memory bounded.
 */
export const contextUsageHistoryAtom = atom<ContextUsageHistoryState>([]);

/**
 * Most recent per-segment character/token breakdown produced by
 * ContextBuilder.buildContext. Used by ContextSizeIndicator and tests to
 * surface which context segments are dominating.
 */
export const contextSegmentSizesAtom = atom<ContextSegmentSizesState>(undefined);

/** Empty chat suggestions state (categorized prompts shown when chat is empty) */
export const emptyChatSuggestionsAtom = atom<EmptyChatSuggestionsState>({
  suggestions: undefined,
  contextHash: undefined,
  loading: false,
  abortController: undefined,
});
